import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  alpha,
  Chip,
  Stack,
  CircularProgress,
  Grid,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Transaction } from '../../types';
import { processMonthlyData, processDailySpending, calculateSpendingInsights, formatCurrency, getTrendColor, MonthlyData, DailySpendingData, SpendingInsights } from '../../utils/chartUtils';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface SpendingPatternsChartProps {
  transactions: Transaction[];
  loading?: boolean;
  timeRange?: 'daily' | 'monthly';
}

const SpendingPatternsChart: React.FC<SpendingPatternsChartProps> = ({
  transactions,
  loading = false,
  timeRange = 'monthly'
}) => {
  const theme = useTheme();

  const chartData = useMemo(() => {
    if (timeRange === 'daily') {
      const dailyData = processDailySpending(transactions, 30);
      return {
        labels: dailyData.map((d: DailySpendingData) => d.label),
        datasets: [
          {
            label: 'Daily Spending',
            data: dailyData.map((d: DailySpendingData) => d.value),
            borderColor: theme.palette.primary.main,
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: theme.palette.primary.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
          }
        ]
      };
    } else {
      const monthlyData = processMonthlyData(transactions, 12);
      return {
        labels: monthlyData.map((d: MonthlyData) => d.month),
        datasets: [
          {
            label: 'Income',
            data: monthlyData.map((d: MonthlyData) => d.income),
            borderColor: theme.palette.success.main,
            backgroundColor: alpha(theme.palette.success.main, 0.1),
            fill: false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.palette.success.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
          },
          {
            label: 'Expenses',
            data: monthlyData.map((d: MonthlyData) => d.expenses),
            borderColor: theme.palette.error.main,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.palette.error.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
          },
          {
            label: 'Savings',
            data: monthlyData.map((d: MonthlyData) => d.savings),
            borderColor: theme.palette.info.main,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            fill: false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: theme.palette.info.main,
            pointBorderColor: theme.palette.background.paper,
            pointBorderWidth: 2,
          }
        ]
      };
    }
  }, [transactions, timeRange, theme]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          color: theme.palette.text.primary,
          font: {
            size: 12,
            family: theme.typography.fontFamily
          }
        }
      },
      tooltip: {
        backgroundColor: alpha(theme.palette.background.paper, 0.95),
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.secondary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = formatCurrency(context.parsed.y);
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
            family: theme.typography.fontFamily
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: alpha(theme.palette.divider, 0.1)
        },
        ticks: {
          color: theme.palette.text.secondary,
          font: {
            size: 11,
            family: theme.typography.fontFamily
          },
          callback: (value: any) => formatCurrency(value)
        }
      }
    },
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    elements: {
      point: {
        hoverBorderWidth: 3
      }
    }
  }), [theme]);

  const insights = useMemo(() => {
    if (transactions.length === 0) return null;
    return calculateSpendingInsights(transactions);
  }, [transactions]);

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
        backdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="600">
          Spending Patterns
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {timeRange === 'daily' ? 'Daily spending trends over the last 30 days' : 'Monthly income, expenses, and savings trends'}
        </Typography>

        {/* Insights Summary */}
        {insights && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(theme.palette.success.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  This Month
                </Typography>
                <Typography variant="h6" fontWeight="600" color="success.main">
                  {formatCurrency(insights.currentMonthExpenses)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(theme.palette.info.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Last Month
                </Typography>
                <Typography variant="h6" fontWeight="600" color="info.main">
                  {formatCurrency(insights.lastMonthExpenses)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(getTrendColor(insights.trendDirection, theme), 0.05),
                  border: `1px solid ${alpha(getTrendColor(insights.trendDirection, theme), 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Monthly Change
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {insights.trendDirection === 'increasing' && <TrendingUpIcon fontSize="small" />}
                  {insights.trendDirection === 'decreasing' && <TrendingDownIcon fontSize="small" />}
                  {insights.trendDirection === 'stable' && <TrendingFlatIcon fontSize="small" />}
                  <Typography variant="h6" fontWeight="600" sx={{ color: getTrendColor(insights.trendDirection, theme) }}>
                    {insights.monthlyChange > 0 ? '+' : ''}{insights.monthlyChange.toFixed(1)}%
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  background: alpha(theme.palette.warning.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.1)}`,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Daily Average
                </Typography>
                <Typography variant="h6" fontWeight="600" color="warning.main">
                  {formatCurrency(insights.avgDailySpending)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Trend Direction Chip */}
        {insights && (
          <Box sx={{ mb: 2 }}>
            <Chip
              icon={
                insights.trendDirection === 'increasing' ? <TrendingUpIcon /> :
                insights.trendDirection === 'decreasing' ? <TrendingDownIcon /> :
                <TrendingFlatIcon />
              }
              label={`Spending is ${insights.trendDirection}`}
              color={
                insights.trendDirection === 'increasing' ? 'error' :
                insights.trendDirection === 'decreasing' ? 'success' :
                'info'
              }
              variant="outlined"
              size="small"
            />
          </Box>
        )}

        {/* Chart */}
        <Box sx={{ height: '300px', position: 'relative' }}>
          <Line data={chartData} options={chartOptions} />
        </Box>

        {/* Top Categories */}
        {insights && insights.topCategories.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Top Spending Categories This Month
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {insights.topCategories.slice(0, 3).map((category: any, index: number) => (
                <Chip
                  key={category.name}
                  label={`${category.name} (${formatCurrency(category.amount)})`}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: alpha(category.color, 0.1),
                    borderColor: alpha(category.color, 0.3),
                    color: category.color,
                    '& .MuiChip-label': {
                      fontWeight: 500
                    }
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default SpendingPatternsChart; 